import React, { useState } from "react";
import { Check, X, Sparkles, Eye, EyeOff, Copy } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { passwordChecklist, generateStrongPassword, PASSWORD_MIN_LENGTH } from "../../lib/passwordRules";

// Reusable strong-password input. Owns:
//  - The masked/unmasked state
//  - The live rule checklist
//  - The "Suggest strong password" generator + copy-to-clipboard
//
// Stays presentation-only — parent owns the value via `value` / `onChange`.
// Wherever this component renders, the user gets identical UX, validation,
// and generation across register / reset / change-password forms.
const PasswordStrengthInput = ({
  value,
  onChange,
  placeholder = "New password",
  testIdBase = "pw",
  autoComplete = "new-password",
  showSuggest = true,
}) => {
  const [reveal, setReveal] = useState(false);
  const checks = passwordChecklist(value);

  const handleSuggest = () => {
    const pw = generateStrongPassword(18);
    onChange(pw);
    setReveal(true);
    // Best-effort copy. Some browsers gate clipboard access on user
    // gesture — the click IS that gesture so we should be fine.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(pw)
        .then(() => toast.success("Strong password generated and copied to clipboard"))
        .catch(() => toast.success("Strong password generated — copy it before submitting"));
    } else {
      toast.success("Strong password generated — copy it before submitting");
    }
  };

  const copyCurrent = () => {
    if (!value || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(value).then(
      () => toast.success("Password copied"),
      () => toast.error("Couldn't copy — select and copy manually"),
    );
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          type={reveal ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-gray-800 border-gray-700 text-white pr-20"
          placeholder={placeholder}
          data-testid={`${testIdBase}-input`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-1 gap-0.5">
          {value ? (
            <button
              type="button"
              onClick={copyCurrent}
              className="p-1.5 text-gray-400 hover:text-white"
              title="Copy"
              data-testid={`${testIdBase}-copy-btn`}
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setReveal((r) => !r)}
            className="p-1.5 text-gray-400 hover:text-white"
            title={reveal ? "Hide" : "Show"}
            data-testid={`${testIdBase}-reveal-btn`}
          >
            {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {showSuggest && (
        <Button
          type="button"
          variant="outline"
          onClick={handleSuggest}
          className="border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 text-amber-300 text-xs h-8"
          data-testid={`${testIdBase}-suggest-btn`}
        >
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Suggest strong password
        </Button>
      )}

      {/* Rule checklist — only render once the user has typed at least
          one character, so empty inputs don't look intimidating. */}
      {value.length > 0 && (
        <ul className="text-[11px] leading-snug space-y-0.5 mt-2" data-testid={`${testIdBase}-checklist`}>
          <Rule ok={checks.length} label={`At least ${PASSWORD_MIN_LENGTH} characters`} />
          <Rule ok={checks.lower}  label="One lowercase letter (a–z)" />
          <Rule ok={checks.upper}  label="One uppercase letter (A–Z)" />
          <Rule ok={checks.digit}  label="One number (0–9)" />
          <Rule ok={checks.symbol} label="One symbol (e.g. ! @ # $ % & * ?)" />
        </ul>
      )}
    </div>
  );
};

const Rule = ({ ok, label }) => (
  <li className={`flex items-center gap-1.5 ${ok ? "text-emerald-400" : "text-gray-500"}`}>
    {ok ? <Check className="h-3 w-3 shrink-0" /> : <X className="h-3 w-3 shrink-0" />}
    <span>{label}</span>
  </li>
);

export default PasswordStrengthInput;
